import { animate, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface CounterProps {
  /** Target numeric value to count up to. */
  value: number;
  /** Duration of the count-up animation in seconds. */
  duration?: number;
  /** Optional prefix, e.g. '+' */
  prefix?: string;
  /** Optional suffix, e.g. 'kg' */
  suffix?: string;
  /** Number of decimal places to show. */
  decimals?: number;
  className?: string;
}

/** Eased count-up number that animates when scrolled into view. Re-triggers if `value` changes while visible. */
export function Counter({ value, duration = 1.6, prefix = '', suffix = '', decimals = 0, className }: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: false, margin: '-40px' });
  const [display, setDisplay] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView) return;
    const from = hasAnimated.current ? display : 0;
    hasAnimated.current = true;
    const controls = animate(from, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(latest),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isInView]);

  const formatted = display.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

export default Counter;
